import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import Table from 'Components/UI/Table';
import BreadCrumb from 'Components/UI/BreadCrumb';
import { ListagemArquivosActions } from 'Store/ListagemArquivos/ListagemArquivos.actions';
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { PlotagemActions } from 'Store/Plotagem/Plotagem.actions';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import {
  breadCrumbs,
  columnsTable,
} from './PlotagemDisciplinas/PlotagemDisciplinas.helpers';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import LoadingDisciplinas from 'Components/UI/CustomLoading/LoadingDisciplinas';
import AvisoPlotagemUpgrade from './AvisoPlotagemUpgrade';
import { ObraActions } from 'Store/Obra/Obra.actions';
import Button from 'Components/UI/Button';
import { Mixpanel } from 'Utils/MixPanel';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import { BackgroundPlanFree, TableWrapper, WithoutConstruction } from './styles';

import emptyPlotImg from 'Static/images/plotagem-discipline-vazio.png';
import { useFeatureFlag } from '../../Hooks/useFeatureFlag';

const PlotagemDisciplinas: FC<Props> = (props) => {
  const { t } = useTranslation('plotagens');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const emptyPlottingUrlFlag = useFeatureFlag('empty-plotting-url');
  const emptyPlottingUrl =
    emptyPlottingUrlFlag.enabled &&
    typeof emptyPlottingUrlFlag?.value === 'string' &&
    !['control', 'test'].includes(emptyPlottingUrlFlag.value)
      ? emptyPlottingUrlFlag.value
      : 'https://coordly.co/blog/controle-de-plotagem-na-maleta-do-engenheiro/';

  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const [selectedDisciplinas, setSelectedDisciplinas] = useState([]);
  const [showErrorSAConnectionInfo, setShowErrorSAConnectionInfo] = useState(false);
  const obraApi = useMemo(() => {
    if (props.obrasList && props.obrasList.length > 0) {
      const currentObra = props.obrasList.find((obra: any) =>
        Number(params.constructionSiteId) === obra.ConstructionSiteId,
      );
      if (currentObra) {
        return currentObra.Api;
      }
    }

    return 0;
  }, [props.obrasList, params.constructionSiteId]);

  const onChange = (selecteds: any) => setSelectedDisciplinas(selecteds);

  const openUpgradePremium = () => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'plotagens',
        originPath: window.location.pathname,
      },
      userInfo: props.userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });

    if (props.userInfo?.CurrentRoleFk === 1) {
      navigate('/faturamento/planos');
    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const handleShowErrorSAConnectionInfo = () => {
    setShowErrorSAConnectionInfo(!showErrorSAConnectionInfo);
  };

  useEffect(() => {
    if (!props.isLoading) {
      setSelectedDisciplinas([]);
    }
  }, [props.isLoading]);

  const rowSelection = {
    selectedDisciplinas,
    onChange,
  };

  useEffect(() => {
    dispatch(PlotagemActions.listDisciplinasPlotagem(Number(params.constructionSiteId)));
    dispatch(ListagemArquivosActions.setShowPlotRequestTable(false));
  }, [params.constructionSiteId, dispatch]);

  useEffect(() => {
    const pathname = location?.pathname || '';
    const search = location?.search;
    dispatch(ObraActions.storePlotagensPath({
      path: pathname,
      params,
      url: `${pathname}${search}`,
    }));
  }, [location, params]);

  if (props.DisciplinaResponse.info && !props.isLoading) {
    breadCrumbs[0] = {
      description: `${props.DisciplinaResponse.info.Name}`,
      href: `/obras/detail/${params.constructionSiteId}`,
    };
    breadCrumbs[1] = {
      description: t('breadcrumb.plotagens'),
      href: `/obras/plotagens/${params.constructionSiteId}`,
    };
  } else {
    breadCrumbs[0] = {
      href: '',
      isLoading: true,
    };
    breadCrumbs[1] = {
      href: '',
      isLoading: true,
    };
  }

  return (
    <>
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.plotagens}
        loading={props.isLoading}
      />
      {props.planFree ? (
        <BackgroundPlanFree>
          <div className='plotagem-free-wrapper'>
            <AvisoPlotagemUpgrade
              onConfirm={openUpgradePremium}
              role={props.userInfo?.CurrentRoleFk}
            />
          </div>
        </BackgroundPlanFree>
      ) : (
        <PageHeader
          name={
            (!props.DisciplinaResponse.data ||
              props.DisciplinaResponse.data.length <= 0) &&
            !props.isLoading
              ? t('page.titlePlotagens')
              : t('page.titleDisciplinas')
          }
        >
          <BreadCrumb breadcrumbs={breadCrumbs} />
          {(!props.DisciplinaResponse.data ||
            props.DisciplinaResponse.data.length <= 0) &&
          !props.isLoading ? (
            <WithoutConstruction>
              <img src={emptyPlotImg} alt={t('page.emptyAlt')} />
              <span className='title'>{t('page.emptyTitle')}</span>
              <Button
                className='buttonEmptyPlot'
                onClick={() =>
                  window.open(emptyPlottingUrl, '_blank', 'noopener,noreferrer')
                }
              >
                {t('page.emptyHelpButton')}
              </Button>
            </WithoutConstruction>
          ) : (
            <TableWrapper>
              {props.isLoading ? (
                <LoadingDisciplinas plotagem multiple={6} />
              ) : (
                props.DisciplinaResponse.data && (
                  <Table
                    columns={columnsTable(
                      props.DisciplinaResponse.data,
                      props.DisciplinaResponse.info,
                      Number(params.constructionSiteId),
                      dispatch,
                      navigate,
                      obraApi,
                      handleShowErrorSAConnectionInfo,
                    )}
                    dataSource={props.DisciplinaResponse.data?.map(
                      (discipline: any, index: number) => ({ ...discipline, key: index }),
                    )}
                    isSpaced={true}
                    rowSelection={rowSelection}
                  />
                )
              )}
            </TableWrapper>
          )}
        </PageHeader>
      )}
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.plotagem,
  planFree: state.auth.planFree,
  userInfo: state.auth.userInfo,
  obrasList: state.obra.ObrasList,
});

const connector = connect(
  mapState,
);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(PlotagemDisciplinas);
